import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { ABOUT, IMPACT_STATS } from "@/data/content";
import Counter from "@/components/common/Counter";
import { TrendingUp, Users, MapPin, Sparkles } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/api";

export default function Impact() {
  const { settings } = useSettings();
  const pageData = settings?.impact_page || {};

  const title = pageData.title || "Jejak Perubahan untuk Masa Depan";
  const desc = pageData.description || "Setiap langkah kecil yang kita ambil bersama dapat menciptakan riak perubahan yang bermakna bagi masyarakat dan lingkungan.";

  return (
    <div data-testid="page-impact">
      <SeoHead
        title={{ id: "Impact", en: "Impact" }}
        description={{ id: desc, en: "Sauyunan Foundation's measurable, integrated, and sustainable impact across 7 strategic areas and 9+ SDGs." }}
      />
      <PageHeader
        eyebrow="Impact"
        title={title}
        description={desc}
        image={getImageUrl("https://images.pexels.com/photos/15773044/pexels-photo-15773044.jpeg")}
      />

      {/* Stats block */}
      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {IMPACT_STATS.map((s, i) => (
              <div key={i} className="card-sf p-8 text-center" data-testid={`impact-stat-${i}`}>
                <div className="text-5xl md:text-6xl font-bold text-[var(--brand-blue)] tracking-tight">
                  <Counter end={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-3 text-sm text-slate-600 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Model detailed */}
      <section className="section-y bg-[var(--bg)]">
        <div className="container-sf">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow mb-3">Sauyunan Impact Model</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
              Lima tahap perjalanan{" "}
              <span className="font-serif-display italic font-normal text-[var(--brand-orange)]">transformasi</span>
            </h2>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-14 left-8 right-8 h-1 bg-gradient-to-r from-[var(--brand-blue)] via-[var(--brand-orange)] to-[var(--brand-blue)] opacity-40 rounded-full" />
            <div className="grid md:grid-cols-5 gap-6">
              {ABOUT.impactModel.map((s, i) => (
                <div key={s.stage} className="relative" data-testid={`impact-model-${s.title.toLowerCase()}`}>
                  <div className={`h-16 w-16 mx-auto rounded-full flex items-center justify-center text-white font-bold shadow-lg text-lg ${i % 2 === 0 ? "bg-[var(--brand-blue)]" : "bg-[var(--brand-orange)]"}`}>
                    {s.stage}
                  </div>
                  <div className="mt-4 text-center">
                    <h4 className="text-lg font-bold text-slate-900">{s.title}</h4>
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Beneficiaries / Areas */}
      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="eyebrow mb-3">Cakupan Program</div>
              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                Menyentuh berbagai lapisan komunitas Nusantara
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed text-lg">
                Program kami menjangkau kelompok rentan, petani, perempuan, pemuda, anak-anak sekolah, komunitas terdampak bencana, dan wirausahawan sosial.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="card-sf p-5">
                  <Users className="h-6 w-6 text-[var(--brand-blue)] mb-3" />
                  <div className="text-2xl font-bold text-slate-900">Multi-kelompok</div>
                  <div className="text-xs text-slate-600 mt-1">Anak, perempuan, petani, pemuda, lansia</div>
                </div>
                <div className="card-sf p-5">
                  <MapPin className="h-6 w-6 text-[var(--brand-orange)] mb-3" />
                  <div className="text-2xl font-bold text-slate-900">Nusantara</div>
                  <div className="text-xs text-slate-600 mt-1">Komitmen dampak lintas provinsi</div>
                </div>
                <div className="card-sf p-5">
                  <TrendingUp className="h-6 w-6 text-[var(--brand-blue)] mb-3" />
                  <div className="text-2xl font-bold text-slate-900">Sustainable</div>
                  <div className="text-xs text-slate-600 mt-1">Dampak berkelanjutan &amp; terukur</div>
                </div>
                <div className="card-sf p-5">
                  <Sparkles className="h-6 w-6 text-[var(--brand-orange)] mb-3" />
                  <div className="text-2xl font-bold text-slate-900">Inovatif</div>
                  <div className="text-xs text-slate-600 mt-1">Teknologi &amp; AI for Humanity</div>
                </div>
              </div>
            </div>
            <div className="relative aspect-square rounded-3xl overflow-hidden shadow-xl">
              <img src="https://images.unsplash.com/photo-1549431971-f54b3f5c82d8?w=1000&q=80" alt="Impact area" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Aspiration 2045 */}
      <section className="section-y bg-gradient-to-br from-[var(--brand-blue)] to-[#254DAA] text-white relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="container-sf relative">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow text-[var(--brand-orange)] mb-3">Aspirasi 2045</div>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">Perjalanan menuju Indonesia Emas</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ABOUT.aspiration2045.map((a, i) => (
              <div key={i} className="rounded-2xl border border-white/15 bg-white/5 p-6 backdrop-blur">
                <div className="text-3xl font-serif-display italic text-[var(--brand-orange)]/70 mb-3">0{i + 1}</div>
                <div className="text-sm md:text-base font-semibold leading-tight">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
